/**
 * Legal document registry — single source of truth for the /legal/* surface.
 *
 * Every legal page on adulab.app reads its metadata from here. Footer + LegalHub
 * iterate over this registry. The build script `scripts/build-legal-pages-registry.js`
 * (Lane 4) generates `docs/generated/legal-pages.md` from this same source.
 *
 * To add a page: append an entry below + create the matching component + add the
 * route in App.jsx. The build script and Footer pick it up automatically.
 */

export type LegalDocumentCategory =
  | 'core'             // Privacy, Cookie, Terms, Service Terms
  | 'commerce'         // Payment, Refund, Advertising
  | 'disclaimer'       // Assessment, Reports, Email, AI, DMCA
  | 'jurisdictional'   // Regional Privacy, DPA
  | 'safety';          // Trust & Safety

export interface LegalDocumentMeta {
  /** URL-friendly slug (e.g., "privacy"). Maps to /legal/{slug}. */
  slug: string;
  /** Display title shown in page header + Footer + LegalHub. */
  title: string;
  /** Full route path mounted in App.jsx. */
  route: string;
  /** ISO date (YYYY-MM-DD) — must align with the visible "Last Updated" string. */
  lastUpdated: string;
  /** Category for grouping in LegalHub + Footer. */
  category: LegalDocumentCategory;
  /** Display order within the category (lower = earlier). */
  displayOrder: number;
  /** Anchor IDs (h2 ids) the page exposes for in-page navigation. */
  sectionAnchors: string[];
}

/**
 * Registry is populated incrementally. Lane 1A added Privacy + Cookie.
 * Lane 1E adds the other 11 migrated pages + 3 new pages (AI Disclosure,
 * DMCA, Refund Policy).
 */
export const LEGAL_DOCUMENT_REGISTRY: LegalDocumentMeta[] = [
  {
    slug: 'privacy',
    title: 'Privacy Policy',
    route: '/legal/privacy',
    lastUpdated: '2026-09-04',
    category: 'core',
    displayOrder: 1,
    sectionAnchors: [
      'information-we-collect',
      'how-we-use-your-information',
      'how-we-share-your-information',
      'data-retention',
      'your-rights',
      'security',
      'children-privacy',
      'changes-to-this-policy',
      'contact-us',
    ],
  },
  {
    slug: 'cookie-policy',
    title: 'Cookie Policy',
    route: '/legal/cookie-policy',
    lastUpdated: '2026-09-04',
    category: 'core',
    displayOrder: 2,
    sectionAnchors: [
      'what-are-cookies',
      'how-we-use-cookies',
      'your-cookie-choices',
      'third-party-cookies',
      'changes-to-this-policy',
      'contact-us',
    ],
  },
  {
    slug: 'terms',
    title: 'Terms of Service',
    route: '/legal/terms',
    lastUpdated: '2026-05-26',
    category: 'core',
    displayOrder: 3,
    sectionAnchors: [
      'acceptance',
      'eligibility',
      'account-registration',
      'subscription-and-billing',
      'use-of-service',
      'intellectual-property',
      'user-content',
      'feedback',
      'third-party-services',
      'disclaimer-of-warranties',
      'limitation-of-liability',
      'indemnification',
      'termination',
      'governing-law',
      'changes-to-terms',
      'contact',
    ],
  },
  {
    slug: 'service-terms',
    title: 'Service Terms',
    route: '/legal/service-terms',
    lastUpdated: '2026-05-26',
    category: 'core',
    displayOrder: 4,
    sectionAnchors: [
      'scope',
      'service-specific-terms',
      'ai-feature-terms',
      'changes',
      'contact',
    ],
  },
  {
    slug: 'payment-terms',
    title: 'Payment Terms',
    route: '/legal/payment-terms',
    lastUpdated: '2026-05-26',
    category: 'commerce',
    displayOrder: 1,
    sectionAnchors: [
      'pricing',
      'billing-cycle',
      'payment-methods',
      'taxes',
      'refunds',
      'failed-payments',
      'changes-to-pricing',
      'cancellation',
      'disputes',
      'contact',
    ],
  },
  {
    slug: 'refund-policy',
    title: 'Refund Policy',
    route: '/legal/refund-policy',
    lastUpdated: '2026-05-26',
    category: 'commerce',
    displayOrder: 2,
    sectionAnchors: [
      'report-product',
      'dynamic-subscription',
      'credit-top-ups',
      'exception-scenarios',
      'how-to-request',
      'contact',
    ],
  },
  {
    slug: 'advertising-terms',
    title: 'Advertising Terms',
    route: '/legal/advertising-terms',
    lastUpdated: '2026-09-04',
    category: 'commerce',
    displayOrder: 3,
    sectionAnchors: [
      'scope',
      'prohibited-content',
      'targeting',
      'metrics-and-reporting',
      'compliance',
      'contact',
    ],
  },
  {
    slug: 'data-processing',
    title: 'Data Processing Addendum',
    route: '/legal/data-processing',
    lastUpdated: '2026-05-26',
    category: 'jurisdictional',
    displayOrder: 1,
    sectionAnchors: [
      'scope',
      'definitions',
      'processor-obligations',
      'sub-processors',
      'data-subject-rights',
      'security',
      'international-transfers',
      'breach-notification',
      'contact',
    ],
  },
  {
    slug: 'do-not-share',
    title: 'Do Not Sell or Share My Personal Information',
    route: '/legal/do-not-share',
    lastUpdated: '2026-09-04',
    category: 'jurisdictional',
    displayOrder: 3,
    sectionAnchors: [],
  },
  {
    slug: 'regional-privacy',
    title: 'Regional Privacy Notice (CCPA / GDPR)',
    route: '/legal/regional-privacy',
    lastUpdated: '2026-09-04',
    category: 'jurisdictional',
    displayOrder: 2,
    sectionAnchors: [
      'california-residents',
      'right-to-know',
      'right-to-delete',
      'right-to-correct',
      'right-to-opt-out-of-sale-or-sharing',
      'how-to-exercise',
      'european-residents',
      'eu-representative',
      'contact',
    ],
  },
  {
    slug: 'email-disclaimer',
    title: 'Email Disclaimer',
    route: '/legal/email-disclaimer',
    lastUpdated: '2026-05-26',
    category: 'disclaimer',
    displayOrder: 1,
    sectionAnchors: [
      'confidentiality',
      'forwarding',
      'unsubscribe',
      'governing-version',
      'contact',
    ],
  },
  {
    slug: 'assessment-disclaimer',
    title: 'Assessment Disclaimer',
    route: '/legal/assessment-disclaimer',
    lastUpdated: '2026-05-26',
    category: 'disclaimer',
    displayOrder: 2,
    sectionAnchors: [
      'informational-not-advice',
      'no-engineer-on-record',
      'jurisdictional-variation',
      'sources-and-limitations',
      'governing-version',
      'contact',
    ],
  },
  {
    slug: 'reports-disclaimer',
    title: 'Reports Disclaimer',
    route: '/legal/reports-disclaimer',
    lastUpdated: '2026-05-26',
    category: 'disclaimer',
    displayOrder: 3,
    sectionAnchors: [
      'informational-not-advice',
      'no-engineer-on-record',
      'ai-generated-content',
      'data-vendor-limitations',
      'governing-version',
      'contact',
    ],
  },
  {
    slug: 'ai-disclosure',
    title: 'AI Disclosure',
    route: '/legal/ai-disclosure',
    lastUpdated: '2026-05-26',
    category: 'disclaimer',
    displayOrder: 4,
    sectionAnchors: [
      'scope',
      'models-and-providers',
      'known-limits',
      'data-handling',
      'output-review',
      'cross-references',
      'contact',
    ],
  },
  {
    slug: 'dmca',
    title: 'DMCA Notice & Takedown',
    route: '/legal/dmca',
    lastUpdated: '2026-05-26',
    category: 'safety',
    displayOrder: 1,
    sectionAnchors: [
      'designated-agent',
      'takedown-notice-requirements',
      'counter-notice-requirements',
      'repeat-infringer-policy',
      'contact',
    ],
  },
  {
    slug: 'trust-safety',
    title: 'Trust & Safety',
    route: '/legal/trust-safety',
    lastUpdated: '2026-05-26',
    category: 'safety',
    displayOrder: 2,
    sectionAnchors: [
      'reporting-abuse',
      'content-moderation',
      'security-research',
      'vulnerability-disclosure',
      'incident-response',
      'contact',
    ],
  },
];

export function getLegalDocument(slug: string): LegalDocumentMeta | null {
  return LEGAL_DOCUMENT_REGISTRY.find((d) => d.slug === slug) ?? null;
}

export function legalDocumentsByCategory(
  category: LegalDocumentCategory
): LegalDocumentMeta[] {
  return LEGAL_DOCUMENT_REGISTRY
    .filter((d) => d.category === category)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}
